import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import type { ComponentProps, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { AddToWatchlistIcon } from '@sorare/core/src/atoms/icons/AddToWatchlistIcon';
import { BookmarkPlus } from '@sorare/core/src/atoms/icons/BookmarkPlus';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { DropCards } from '@sorare/core/src/atoms/icons/DropCards';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import type { StackProps } from '@sorare/core/src/lib/algolia';
import { isSamePlayerSameSeason } from '@sorare/core/src/lib/cards';

import { AddTokenToDeck } from 'components/decks/AddTokenToDeck';
import { DeckEditorDialog } from 'components/decks/DeckEditorDialog';
import { Empty } from 'components/market/Empty';
import {
  GridOverlayLoadingIndicator,
  MarketGrid,
} from 'components/market/Grid';
import type { Props as PlayerDrawerProps } from 'components/player/PlayerDrawer';
import { PlayerDrawer } from 'components/player/PlayerDrawer';
import { Token } from 'components/token/Token';
import { TokenUnsealButton } from 'components/token/TokenUnsealButton';
import { AddToWatchlistButton } from 'components/watchlists/AddToWatchlistButton';
import { useMarketplaceContext } from 'contexts/Marketplace';
import { useCurrentMarketplaceUserContext } from 'contexts/currentMarketplaceUser';
import { useIsBoostAffordable } from 'hooks/useIsBoostAffordable';
import { useIsCardDroppable } from 'hooks/useIsCardDroppable';
import type { MyCardsSelectionMode } from 'lib/my-cards';
import type { CardResultsProps } from 'searchCards/AdvancedCardSearch/types';
import { Pagination } from 'searchCards/Pagination';

import type { CardPreviewGrid_card } from './__generated__/index.graphql';

type Item = CardPreviewGrid_card & { stack?: StackProps };

type Props = {
  item: Item;
  setCardOpened: (cardSlug: string, playerSlug: string) => void;
} & Pick<
  ComponentProps<typeof Token>,
  | 'galleryOwnerSlug'
  | 'hideOwner'
  | 'hideSorareUser'
  | 'stack'
  | 'hideDetails'
  | 'action'
  | 'onlyPrimary'
  | 'selectionMode'
  | 'totalXpNeeded'
  | 'onToggleCard'
  | 'selected'
  | 'forceShowActionButton'
  | 'isDoublonOfSelectedCardSlug'
>;

const TokenItem = ({ item, setCardOpened, ...tokenProps }: Props) => {
  const onInfoClick = useCallback(() => {
    setCardOpened(item.slug, item.anyPlayer.slug);
  }, [item.slug, item.anyPlayer.slug, setCardOpened]);

  return (
    <Token
      card={item}
      onInfoClick={onInfoClick}
      stack={item.stack}
      {...tokenProps}
    />
  );
};

const getItemKey = (item: Item) => {
  return item.slug!;
};

type GridProps = Pick<
  CardResultsProps,
  'topic' | 'showDesktopFilter' | 'editableLists' | 'galleryOwnerSlug'
> & {
  items: Item[] | undefined;
  loading: boolean;
  sport: Sport;
  selectionMode?: MyCardsSelectionMode;
  setSelectionMode?: (mode: MyCardsSelectionMode) => void;
  selectedCardSlugs?: string[];
  canDrop?: boolean;
  canLevelUp?: boolean;
  hideScoutShortcuts?: boolean;
  showActionButtonOnFirstCard?: boolean;
  prependedItem?: ReactNode;
} & Pick<
    ComponentProps<typeof TokenItem>,
    | 'galleryOwnerSlug'
    | 'hideOwner'
    | 'hideSorareUser'
    | 'onlyPrimary'
    | 'onToggleCard'
    | 'totalXpNeeded'
  >;

export const CardPreviewGrid = ({
  items,
  topic,
  loading,
  showDesktopFilter,
  editableLists,
  sport,
  selectionMode: gridSelectionMode,
  setSelectionMode,
  onToggleCard,
  selectedCardSlugs,
  canDrop,
  canLevelUp,
  hideScoutShortcuts = false,
  galleryOwnerSlug,
  showActionButtonOnFirstCard,
  prependedItem,
  ...tokenItemProps
}: GridProps) => {
  const { hideDetails } = useMarketplaceContext();
  const { userSportProfile } = useCurrentMarketplaceUserContext();
  const { generateSportContextPath } = useSportContext();

  const isCardDroppable = useIsCardDroppable();
  const isBoostAffordable = useIsBoostAffordable();

  const [createListDialogOpen, setCreateListDialogOpen] = useState(false);
  const [addCardToDeck, setAddCardToDeck] =
    useState<CardPreviewGrid_card | null>(null);
  const [cardOpened, setCardOpened] =
    useState<PlayerDrawerProps['cardOpened']>(null);

  const { up: isTabletOrAbove } = useScreenSize('tablet');

  if (items?.length === 0) {
    return <Empty isGallery={!!galleryOwnerSlug} topic={topic} />;
  }

  return (
    <>
      <MarketGrid>
        {prependedItem}
        {items?.map((item, i) => {
          const isSealed = item.sealed;
          const selectionMode = item.loanedToken?.id
            ? undefined
            : gridSelectionMode;
          const noSelectionMode = !selectionMode;
          const showAddToDeck =
            noSelectionMode &&
            editableLists &&
            item.rarityTyped !== Rarity.common &&
            !isSealed;

          const showDrop =
            noSelectionMode && canDrop && setSelectionMode && !isSealed;
          const showLevelUp =
            noSelectionMode && canLevelUp && setSelectionMode && !isSealed;

          const showPlayerPageShortcuts =
            noSelectionMode && !hideScoutShortcuts;
          const showAddToWatchlist = noSelectionMode && !hideScoutShortcuts;

          const showSealing =
            item.rarityTyped !== Rarity.common &&
            !isSealed &&
            (noSelectionMode ||
              (selectionMode === 'sealing' && item.sealable)) &&
            setSelectionMode;
          const { isDroppable } = isCardDroppable(item);
          const { isBoostable, xpNeeded } = isBoostAffordable(item);

          const isSelected = selectedCardSlugs?.includes(item.slug);

          const isDoublonOfSelectedCardSlug =
            !isSelected &&
            sport === Sport.BASEBALL &&
            selectedCardSlugs?.some(selectedCardSlug =>
              isSamePlayerSameSeason(selectedCardSlug, item.slug)
            );

          return (
            <TokenItem
              key={getItemKey(item)}
              item={item}
              forceShowActionButton={
                (showActionButtonOnFirstCard && i === 0) ||
                (showSealing && isSelected)
              }
              setCardOpened={(cardSlug, playerSlug) =>
                setCardOpened({
                  cardSlug,
                  playerSlug,
                })
              }
              hideDetails={!!(hideDetails && galleryOwnerSlug)}
              isDoublonOfSelectedCardSlug={isDoublonOfSelectedCardSlug}
              action={
                showPlayerPageShortcuts ||
                showAddToWatchlist ||
                showAddToDeck ||
                showDrop ||
                showLevelUp ||
                showSealing ||
                isSealed ? (
                  <Vertical>
                    {!!showPlayerPageShortcuts && (
                      <IconButton
                        icon={faUser}
                        to={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
                          params: { slug: item.anyPlayer.slug },
                        })}
                        size="small"
                        color="secondary"
                      />
                    )}
                    {!!showAddToWatchlist && (
                      <AddToWatchlistButton
                        variant="icon"
                        anyPlayer={item.anyPlayer}
                        size="small"
                        container
                        color="secondary"
                        startIcon={<AddToWatchlistIcon />}
                      />
                    )}
                    {!!showAddToDeck && (
                      <IconButton
                        onClick={() => {
                          if (!userSportProfile?.decks?.nodes?.length) {
                            setCreateListDialogOpen(true);
                          }
                          setAddCardToDeck(item);
                        }}
                        color="secondary"
                        size="small"
                        disabled={!noSelectionMode}
                      >
                        <BookmarkPlus />
                      </IconButton>
                    )}
                    {!!showDrop && (
                      <OnboardingHighlightWrapper
                        wrap={
                          !!showActionButtonOnFirstCard &&
                          i === 0 &&
                          isTabletOrAbove
                        }
                        highlightName="DropCardCta"
                      >
                        <IconButton
                          onClick={() => {
                            setSelectionMode('drop');
                            if (isDroppable) {
                              onToggleCard?.(item.slug);
                            }
                          }}
                          color="secondary"
                          size="small"
                          disabled={!noSelectionMode}
                        >
                          <DropCards size={2} />
                        </IconButton>
                      </OnboardingHighlightWrapper>
                    )}
                    {!!showLevelUp && (
                      <IconButton
                        onClick={() => {
                          setSelectionMode('boost');
                          if (isBoostable) {
                            onToggleCard?.(item.slug, { xpNeeded });
                          }
                        }}
                        color="secondary"
                        size="small"
                        disabled={!noSelectionMode}
                      >
                        <Boost size={2} />
                      </IconButton>
                    )}
                    {!!showSealing && !isDoublonOfSelectedCardSlug && (
                      <ConditionalWrapper
                        Wrapper={Tooltip}
                        wrap={!item.sealable}
                        props={{
                          title: (
                            <FormattedMessage
                              id="Sealing.explanation.sealingDoublon"
                              defaultMessage="You already sealed the same edition of this card"
                            />
                          ),
                        }}
                      >
                        <IconButton
                          disabled={!item.sealable}
                          onClick={() => {
                            setSelectionMode?.('sealing');
                            onToggleCard?.(item.slug, {
                              sealableFor: item.sealableFor || 0,
                            });
                          }}
                          color={isSelected ? 'purple' : 'secondary'}
                          size="small"
                        >
                          {isSelected ? (
                            <Vault
                              size={2}
                              action="unselect"
                              color="var(--c-white)"
                            />
                          ) : (
                            <Vault size={2} action="vault" />
                          )}
                        </IconButton>
                      </ConditionalWrapper>
                    )}
                    {!!setSelectionMode && <TokenUnsealButton item={item} />}
                  </Vertical>
                ) : undefined
              }
              selectionMode={selectionMode}
              selected={isSelected}
              onToggleCard={onToggleCard}
              galleryOwnerSlug={galleryOwnerSlug}
              {...tokenItemProps}
            />
          );
        })}
      </MarketGrid>
      <Pagination visible={!!items?.length} />
      {!!loading && <GridOverlayLoadingIndicator />}
      {!!addCardToDeck &&
        !createListDialogOpen &&
        addCardToDeck.rarityTyped !== Rarity.common && (
          <AddTokenToDeck
            card={addCardToDeck}
            onClose={() => setAddCardToDeck(null)}
            addList={() => setCreateListDialogOpen(true)}
          />
        )}
      {!!createListDialogOpen && (
        <DeckEditorDialog
          sport={sport}
          onClose={() => {
            setAddCardToDeck(addCardToDeck);
            setCreateListDialogOpen(false);
          }}
        />
      )}
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
      />
    </>
  );
};

CardPreviewGrid.fragments = {
  card: gql`
    fragment CardPreviewGrid_card on AnyCardInterface {
      slug
      sealed
      sealable
      sealableFor
      sport
      anyPlayer {
        slug
        ...AddToWatchlistButton_anyPlayer
      }
      loanedToken {
        id
      }
      ...AddTokenToDeck_anyCard @include(if: $editableLists)
      ...Token_anyCard
      ...useIsCardDroppable_anyCard
      ...useIsBoostAffordable_anyCard
      ...TokenUnsealButton_anyCard
    }
    ${Token.fragments.anyCard}
    ${AddTokenToDeck.fragments.anyCard}
    ${useIsCardDroppable.fragments.anyCard}
    ${useIsBoostAffordable.fragments.anyCard}
    ${TokenUnsealButton.fragments.anyCard}
    ${AddToWatchlistButton.fragments.anyPlayer}
  ` as TypedDocumentNode<CardPreviewGrid_card>,
};
